'use client';

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * The product demo, embedded in-page as a click-to-load facade.
 *
 * WHY A FACADE AND NOT A PLAIN <iframe>
 *
 * A YouTube iframe is not a video player — it is a whole second web page.
 * Dropped straight into the homepage it costs roughly 500KB+ of third-party
 * JavaScript, a dozen extra connections, and main-thread work that competes
 * with the page's own hydration. This site's FCP/LCP were expensive to win
 * (see the root-layout-must-stay-static rule), and one embed below the fold
 * is enough to give them back. So nothing from Google is requested until the
 * visitor actually asks for the video by clicking.
 *
 * That single decision also settles a legal question, which is the reason it
 * is not merely a nice-to-have here. SendVC is operated from Germany and
 * publishes GDPR compliance as a selling point. A YouTube iframe contacts
 * Google and sets identifiers the moment it renders — before consent, on
 * every visitor, whether or not they ever watch. The facade means that
 * contact happens only on a deliberate click, which IS the consent.
 * `youtube-nocookie.com` narrows what is set even then.
 *
 * WHY THE POSTER IS A LOCAL FILE
 *
 * The obvious poster source is `i.ytimg.com/vi/<id>/maxresdefault.jpg`, but
 * hotlinking it would reintroduce the exact third-party request the facade
 * exists to avoid, and `next.config` only permits Vercel Blob as a remote
 * image host anyway. The thumbnail is therefore checked in at
 * `public/demo-poster.jpg` and served from our own origin, optimised by
 * next/image like any other local asset.
 *
 * The `<noscript>` fallback is a plain link rather than an iframe: with no JS
 * there is no click to consent with, so we send the visitor to YouTube
 * instead of embedding it for them.
 */

/**
 * CSP COUPLING — READ BEFORE CHANGING THE EMBED HOST.
 *
 * The player is framed from youtube-nocookie.com, which only works because
 * `frame-src` in next.config.js lists it. Changing the host here without
 * changing it there yields a blank box and this console error:
 *   "Refused to load ... because it does not appear in the frame-src
 *    directive of the Content Security Policy"
 * Nothing fails at build time, nothing fails server-side, and the served
 * HTML looks correct — the facade means the iframe does not exist until a
 * human clicks. So this breakage is invisible to markup checks and is only
 * observable by actually clicking play in a browser. Do that after any
 * change here.
 */

/** youtu.be/zI9zzxCRnSU — the demo. Confirmed playable and embeddable via
 *  YouTube's oembed endpoint, which is also the cheapest way to test a
 *  YouTube URL: it 404s for dead/removed ids where the watch page still
 *  returns HTTP 200. */
const VIDEO_ID = 'zI9zzxCRnSU';

const DemoVideo: React.FC = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Move focus into the player once it mounts, so a keyboard user who pressed
  // Enter on the poster is not left with focus on a button that no longer
  // exists. Without this the tab order silently resets to the top of the page.
  useEffect(() => {
    if (isPlaying) iframeRef.current?.focus();
  }, [isPlaying]);

  return (
    <section id="demo" className="py-20 bg-white">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium text-primary-700 bg-primary-50 rounded-full">
            {t('pageContent.home.demo.tagline')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('pageContent.home.demo.title')}
          </h2>
          <p className="text-lg text-gray-600">{t('pageContent.home.demo.subtitle')}</p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* aspect-video reserves the box before anything loads, so swapping
              the poster for the iframe shifts nothing. CLS stays at zero. */}
          <div className="relative aspect-video overflow-hidden rounded-2xl bg-gray-900 shadow-xl ring-1 ring-gray-900/5">
            {isPlaying ? (
              <iframe
                ref={iframeRef}
                // nocookie + autoplay: the click already happened, so starting
                // playback is what the visitor asked for, not an autoplay ad.
                src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                title={t('pageContent.home.demo.title')}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute inset-0 h-full w-full border-0"
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsPlaying(true)}
                aria-label={t('pageContent.home.demo.play')}
                className="group absolute inset-0 h-full w-full cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              >
                <Image
                  src="/demo-poster.jpg"
                  alt=""
                  fill
                  // Below the fold: no priority, no preload. It must never
                  // compete with the hero for LCP.
                  sizes="(max-width: 896px) 100vw, 896px"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/95 shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    {/* translate-x nudges the optical centre: a triangle
                        centred by its bounding box looks left-heavy. */}
                    <Play className="h-8 w-8 translate-x-0.5 fill-primary-600 text-primary-600" />
                  </span>
                </span>
              </button>
            )}
          </div>

          <noscript>
            <p className="mt-4 text-center text-sm text-gray-600">
              <a
                href={`https://www.youtube.com/watch?v=${VIDEO_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 underline"
              >
                {t('pageContent.home.demo.play')}
              </a>
            </p>
          </noscript>
        </div>
      </div>
    </section>
  );
};

export { DemoVideo };
